/**
 * @file        src/pages/account/AbandonedPayments.jsx
 * @description Account Management → Abandoned Payments. Every checkout a
 *              customer STARTED and the gateway then reported cancelled,
 *              failed or dropped — flight, hotel, insurance, post-booking
 *              add-ons and reschedules — with the contact details needed to
 *              ring them and ask why, and a follow-up state so two people do
 *              not call the same customer twice.
 *
 *              Its own screen, over its own table (`abandoned_payments`,
 *              migration 065), deliberately NOT a new status in the Transaction
 *              Log: that ledger's rows mean money moved, and it feeds the
 *              Balance Sheet, reports and every invoice. An abandoned checkout
 *              captured nothing, so adding it there would have changed totals
 *              on screens nobody asked us to touch.
 *
 *              WHOLE-BUSINESS on purpose, not split B2C / Corporate like the
 *              booking lists and dashboards (§9d): most rows are guest
 *              checkouts with no user at all, so an audience split would file
 *              the majority of leads under neither screen. "Customer" instead
 *              says Guest / Retail / Corporate per row and can be filtered,
 *              which is what decides who should make the call.
 * @author      Kinetika Labs
 * @created     2026-09-22
 * @changelog
 *   - 2026-09-25 Claude — Dropped the "Recovered" KPI card: the client tracks open
 *                          leads, their value and how many have been contacted,
 *                          and nothing else. Display only — the `recovered`
 *                          outcome, its badge, its filter and booking_backend's
 *                          automatic close are all unchanged, because a lead that
 *                          is not closed when the customer pays is a call to
 *                          somebody who has already booked.
 *   - 2026-09-22 Claude — Initial (cancelled-checkout follow-up queue).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Select from '../../components/Select';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { useBrands } from '../../api/brands';
import { qk } from '../../api/queryKeys';
import {
  listAbandonedPayments,
  abandonedPaymentTotals,
  setAbandonedPaymentOutcome,
} from '../../api/abandonedPayments';
import { inr, formatDate, formatDateTime } from '../../lib/format';
import { toast } from '../../lib/toast';

const FLOW_OPTIONS = [
  { value: '', label: 'All products' },
  { value: 'flight', label: 'Flight' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'ssr', label: 'Add-ons' },
  { value: 'reissue', label: 'Reschedule' },
];
const FLOW_LABEL = {
  flight: 'Flight', hotel: 'Hotel', insurance: 'Insurance',
  ssr: 'Add-ons', reissue: 'Reschedule',
};

const OUTCOME_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'recovered', label: 'Recovered' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'unreachable', label: 'Unreachable' },
];
// 'recovered' is absent on purpose: booking_backend sets it when a retry on the
// same transaction actually pays, so it is a fact, not a judgement. The API
// rejects it from here too — someone who learns a customer booked another way
// writes that in the note.
const SETTABLE_OUTCOMES = [
  { value: 'open', label: 'Open — still to call' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'unreachable', label: 'Unreachable' },
];
const OUTCOME_LABEL = Object.fromEntries(OUTCOME_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]));
const OUTCOME_TONE = {
  open: 'warn', contacted: 'pend', recovered: 'ok',
  not_interested: 'rej', unreachable: 'rej',
};

const CUSTOMER_OPTIONS = [
  { value: '', label: 'All customers' },
  { value: 'guest', label: 'Guest' },
  { value: 'retail', label: 'Retail' },
  { value: 'corporate', label: 'Corporate' },
];
const CUSTOMER_LABEL = { guest: 'Guest', retail: 'Retail', corporate: 'Corporate' };

// Easebuzz's own verdict, translated for a human but never replaced by one: an
// unrecognised status is shown AS STORED, because "why didn't they pay" is the
// entire question this screen exists to answer and a new gateway string must be
// visible the day it first arrives, not disguised as one we already know.
const REASON_LABEL = {
  usercancelled: 'Customer cancelled',
  userCancelled: 'Customer cancelled',
  failure: 'Payment failed',
  failed: 'Payment failed',
  pending: 'Left pending',
  bounced: 'Dropped before paying',
};
const reasonLabel = (s) => REASON_LABEL[String(s || '')] || REASON_LABEL[String(s || '').toLowerCase()] || s || '—';

function Field({ label, children }) {
  return (
    <div><span className="muted">{label}</span><div>{children || <span className="muted">—</span>}</div></div>
  );
}

/**
 * The whole lead plus the follow-up form. Fed from the list row rather than a
 * second fetch — the list endpoint already returns every column — except that
 * saving returns the refreshed row, which is what the list then invalidates on.
 */
function FollowUpModal({ row, brandLabel, onClose }) {
  const qc = useQueryClient();
  const [outcome, setOutcome] = useState(row?.outcome === 'recovered' ? 'contacted' : (row?.outcome || 'open'));
  const [note, setNote] = useState(row?.note || '');

  const mut = useMutation({
    mutationFn: () => setAbandonedPaymentOutcome(row.id, { outcome, note }),
    onSuccess: (res) => {
      if (res?.data?.unchanged) {
        toast.ok('Already recovered', 'This customer has since paid — the lead was left closed.');
      } else {
        toast.ok('Saved', 'Follow-up recorded.');
      }
      qc.invalidateQueries({ queryKey: ['abandoned-payments'] });
      onClose();
    },
    onError: (e) => toast.err('Could not save', e.response?.data?.error?.message || 'Please try again.'),
  });

  if (!row) return null;
  const recovered = row.outcome === 'recovered';

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass">
        <div className="lk-modal-head">
          <h3>{FLOW_LABEL[row.flow] || row.flow} · {inr(row.amount)}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>

        <div className="det-body">
          <h4 className="det-h">Customer</h4>
          <div className="det-grid">
            <Field label="Name">{row.customer_name}</Field>
            <Field label="Type">{CUSTOMER_LABEL[row.customer_type] || row.customer_type}</Field>
            <Field label="Email">
              {row.customer_email ? <a href={`mailto:${row.customer_email}`}>{row.customer_email}</a> : null}
            </Field>
            <Field label="Phone">
              {row.customer_phone ? <a href={`tel:${row.customer_phone}`}>{row.customer_phone}</a> : null}
            </Field>
          </div>

          <h4 className="det-h">What they were buying</h4>
          <div className="det-grid">
            <Field label="Product">{FLOW_LABEL[row.flow] || row.flow}</Field>
            <Field label="Amount">{inr(row.amount)}</Field>
            <Field label="Trip">{row.trip_summary}</Field>
            <Field label="Travel date">{row.travel_date ? formatDate(row.travel_date) : null}</Field>
            <Field label="Brand">{brandLabel(row.brand)}</Field>
            {/* Add-ons and reschedules amend a booking that already exists and is
                already paid for — the reference is the useful half of those rows. */}
            <Field label="Existing booking">
              {row.booking_reference ? <span className="mono">{row.booking_reference}</span> : null}
            </Field>
          </div>

          <h4 className="det-h">The payment</h4>
          <div className="det-grid">
            <Field label="Reason">{reasonLabel(row.gateway_status)}</Field>
            <Field label="Gateway message">{row.gateway_error}</Field>
            <Field label="Transaction"><span className="mono">{row.txnid}</span></Field>
            <Field label="Attempts">{row.attempts}</Field>
            <Field label="First attempt">{formatDateTime(row.first_seen_at)}</Field>
            <Field label="Last attempt">{formatDateTime(row.last_seen_at)}</Field>
          </div>

          <h4 className="det-h">Follow-up</h4>
          {recovered ? (
            // Nothing to do, and deliberately no form: the customer retried on
            // the same transaction and paid. Re-opening the lead would put
            // somebody who has booked back into the call queue.
            <p className="muted">
              This customer came back and paid on {formatDateTime(row.recovered_at)} — no call needed.
            </p>
          ) : (
            <>
              <div className="det-grid">
                <Field label="Last handled by">{row.handled_by}</Field>
                <Field label="Last handled">{row.handled_at ? formatDateTime(row.handled_at) : null}</Field>
              </div>
              <div className="field">
                <label>Outcome</label>
                <Select value={outcome} onChange={setOutcome} options={SETTABLE_OUTCOMES} />
              </div>
              <div className="field">
                <label>Note — what did they say?</label>
                <textarea
                  value={note}
                  maxLength={500}
                  placeholder="e.g. card declined twice, will try UPI tonight · found a cheaper fare · wrong dates"
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
                <button type="button" className="btn primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
                  {mut.isPending ? 'Saving…' : 'Save follow-up'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AbandonedPayments() {
  const { options: brandOptions, isLoading: brandsLoading, labelOf } = useBrands();

  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [brand, setBrand] = useState('');
  const [flow, setFlow] = useState('');
  // Defaults to the working queue rather than the whole archive — this screen is
  // a call list, and "everything that ever failed" is not one.
  const [outcome, setOutcome] = useState('open');
  const [customerType, setCustomerType] = useState('');
  const [hasContact, setHasContact] = useState(false);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [detailRow, setDetailRow] = useState(null);

  const brandFilterOptions = [
    { value: '', label: 'All brands' },
    // A row whose storefront could not be resolved: the checkout context had
    // already been purged when the callback landed. Rare, but findable.
    { value: 'unknown', label: 'Unknown' },
    ...brandOptions,
  ];
  const brandLabel = (b) => (b === 'unknown' || b === null || b === undefined ? 'Unknown' : labelOf(b));

  // ONE filter object for the table and the KPI strip, so a card can never
  // disagree with the rows beneath it (the same rule as the Booking List
  // summary, §9d). Only `page` differs, which is why it is applied separately.
  const filters = {
    ...(brand ? { brand } : {}),
    ...(flow ? { flow } : {}),
    ...(outcome ? { outcome } : {}),
    ...(customerType ? { customerType } : {}),
    ...(hasContact ? { hasContact: 'true' } : {}),
    ...(appliedSearch ? { search: appliedSearch } : {}),
    ...(from ? { from } : {}),
    // Inclusive end-of-day — a bare date compares as 00:00:00 and would
    // silently drop every lead from the "to" day itself.
    ...(to ? { to: `${to} 23:59:59.999` } : {}),
  };
  const listParams = { ...filters, page, pageSize };

  const { data: list, isLoading } = useQuery({
    queryKey: qk.abandonedPayments.list(listParams),
    queryFn: () => listAbandonedPayments(listParams),
    placeholderData: keepPreviousData,
  });

  const { data: totalsRes } = useQuery({
    queryKey: qk.abandonedPayments.totals(filters),
    queryFn: () => abandonedPaymentTotals(filters),
  });

  const body = list?.data || {};
  const rows = body.rows || [];
  const total = body.total || 0;
  const t = totalsRes?.data;

  function onSearchSubmit(e) {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  }

  function applyFilter(setter) {
    return (v) => { setter(v); setPage(1); };
  }

  return (
    <>
      <PageHead
        title="Abandoned Payments"
        subtitle="Account Management · checkouts the customer started and the gateway reported cancelled or failed — call them and find out why"
      />

      <div className="mini-kpi">
        <article className="mk glass neo">
          <small>Open Leads</small><b>{t ? t.open_leads : '—'}</b>
        </article>
        <article className="mk glass neo">
          <small>Open Value</small><b className="cre">{t ? inr(t.open_value) : '—'}</b>
        </article>
        <article className="mk glass neo">
          <small>Contacted</small><b>{t ? t.contacted : '—'}</b>
        </article>
        {/* No "Recovered" card: the client does not want self-recovery tracked
            here (2026-09-25). The OUTCOME itself stays — booking_backend closes a
            lead the moment that txnid is paid (§16 markRecovered), and without
            that close the row would sit in this queue as an open lead and
            somebody would ring a customer who has already booked. The summary
            API still returns recovered / recovered_value; they are simply not
            displayed, which is also what keeps the three cards above counting a
            partition of the leads rather than overlapping sets. */}
      </div>

      <article className="panel glass">
        <form className="search-row" onSubmit={onSearchSubmit}>
          <div className="field">
            <label>Name / Email / Phone / Txn</label>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="field">
            <label>Product</label>
            <Select value={flow} onChange={applyFilter(setFlow)} options={FLOW_OPTIONS} />
          </div>
          <div className="field">
            <label>Outcome</label>
            <Select value={outcome} onChange={applyFilter(setOutcome)} options={OUTCOME_OPTIONS} />
          </div>
          <div className="field">
            <label>Customer</label>
            <Select value={customerType} onChange={applyFilter(setCustomerType)} options={CUSTOMER_OPTIONS} />
          </div>
          <div className="field">
            <label>Brand</label>
            <Select
              value={brand}
              onChange={applyFilter(setBrand)}
              options={brandFilterOptions}
              placeholder={brandsLoading ? 'Loading…' : 'All brands'}
            />
          </div>
          <div className="field">
            <label>From</label>
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
          </div>
          <div className="field">
            <label>To</label>
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
          </div>
          <div className="field">
            <label>Reachable</label>
            <label className="check">
              <input
                type="checkbox"
                checked={hasContact}
                onChange={(e) => { setHasContact(e.target.checked); setPage(1); }}
              />
              <span>Has phone or email</span>
            </label>
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr>
                  <th>Last Attempt</th><th>Product</th><th>Customer</th><th>Trip</th>
                  <th>Amount</th><th>Why</th><th>Tries</th><th>Outcome</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={9} className="empty">No abandoned payments for these filters.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id}>
                    <td className="muted">{formatDateTime(r.last_seen_at)}</td>
                    <td>
                      <div>{FLOW_LABEL[r.flow] || r.flow}</div>
                      <small className="muted">{brandLabel(r.brand)}</small>
                    </td>
                    <td>
                      <div>{r.customer_name || <span className="muted">—</span>}</div>
                      {/* Click-to-contact is the whole point of the screen: the
                          admin team works this list on a phone or a mail client,
                          not by copying strings out of a table. */}
                      {r.customer_phone ? <div><a href={`tel:${r.customer_phone}`}>{r.customer_phone}</a></div> : null}
                      {r.customer_email ? (
                        <small><a href={`mailto:${r.customer_email}`}>{r.customer_email}</a></small>
                      ) : null}
                      <div><small className="muted">{CUSTOMER_LABEL[r.customer_type] || r.customer_type}</small></div>
                    </td>
                    <td style={{ maxWidth: 260 }}>
                      <div
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={r.trip_summary || ''}
                      >
                        {r.trip_summary || <span className="muted">—</span>}
                      </div>
                      {r.travel_date ? <small className="muted">Travels {formatDate(r.travel_date)}</small> : null}
                      {r.booking_reference ? <small className="muted mono"> · {r.booking_reference}</small> : null}
                    </td>
                    <td>{inr(r.amount)}</td>
                    <td>
                      <div>{reasonLabel(r.gateway_status)}</div>
                      {r.gateway_error ? (
                        <small
                          className="muted"
                          style={{ display: 'inline-block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={r.gateway_error}
                        >
                          {r.gateway_error}
                        </small>
                      ) : null}
                    </td>
                    <td>{r.attempts}</td>
                    <td>
                      <Chip tone={OUTCOME_TONE[r.outcome] || 'pend'}>
                        {OUTCOME_LABEL[r.outcome] || r.outcome}
                      </Chip>
                      {r.note ? (
                        <div>
                          <small
                            className="muted"
                            style={{ display: 'inline-block', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={r.note}
                          >
                            {r.note}
                          </small>
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button type="button" className="btn xs ghost" onClick={() => setDetailRow(r)}>
                          <Icon name="search" /> {r.outcome === 'recovered' ? 'Details' : 'Follow up'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>

      {detailRow ? (
        <FollowUpModal
          // Remounts per lead so the outcome/note form starts from THAT row's
          // stored values instead of keeping the previous one's state.
          key={detailRow.id}
          row={detailRow}
          brandLabel={brandLabel}
          onClose={() => setDetailRow(null)}
        />
      ) : null}
    </>
  );
}
